<template>
    <div
      id="home"
      :style="{ backgroundColor: siteColour }"
      :class="{ greysite: this.isRobotSelfieSite,
               'has-translations': this.hasTranslationLinks }"
    >

    <div class="loading" v-if="loading || error">
      <LoadingSpinner class="loading-spinner" v-if="loading" />
      <div class="error" v-if="error && !loading">
        <p v-if="error === 404">
          Sorry we couldn't find that site.
        </p>
        <p v-else>
          Something went wrong trying to load the content for this. It's probably a
          temporary blip, so could you try refreshing your browser please?
        </p>
      </div>
    </div>

    <Confirmed v-if="confirmed && !loading" :watchButton="content.confirmation_text_button">
      <div v-html="content.confirmation_text"></div>
    </Confirmed>

    <div class="content" v-if="!confirmed && content && !loading">
      <SiteHeader :navLinks="navLinks" :translationLinks="translationLinks" siteColour />
      <SectionTop
        :backgroundImage=backgroundImage
        :headingText=this.content.title
        :extraText=this.content.header_extra
        :callToActionLabel=content.header_cta_label
      />
      <div class="container">
        <section id="start" v-if="content.intro" v-html="content.intro" class="intro-section"></section>

        <section v-if="content.draw_content" class="home-image-section image-right" id="draw">
          <div class="image">
            <LenseImage
              v-if="!content.draw_image"
              :startPerc="content.lense_start_perc"
              :endPerc="content.lense_end_perc"
              :innerZoomStart="content.lense_inner_zoom_start"
              :innerZoomEnd="content.lense_inner_zoom_end"
              :outerZoomStart="content.lense_outer_zoom_start"
              :outerZoomEnd="content.lense_outer_zoom_end"
              :innerRotationStart="content.lense_inner_rotation_start"
              :innerRotationEnd="content.lense_inner_rotation_end"
              :outerRotationStart="content.lense_outer_rotation_start"
              :outerRotationEnd="content.lense_outer_rotation_end"
            />
            <img
              v-if="content.draw_image"
              :src="content.draw_image.meta.download_url"
              :alt="content.draw_image.title"
              :title="content.draw_image.title"
            />
          </div>

          <div class="content">
            <div v-html="content.draw_content"></div>

            <p class="cta-container" v-if="content.draw_enable_robot_selfie">
              <SwitchButton @click="toggleRobotSelfieModal" :text="content.draw_enable_label" :active-text="content.draw_disable_label" :active="robotSelfieModalShowing" />
            </p>
          </div>
        </section>

        <Modal ref="robotSelfieModal">
          <RobotSelfie
            ref="robotSelfie"
            @selfieSaved="handleSelfieSaved"
            :site="site"
            :videoErrorHeading="content.video_error_heading"
            :videoErrorText1="content.video_error_content"
            :videoErrorText2="content.video_error_content2"
            :videoStartButton="content.video_start_button"
            :videoLoadingText="content.video_loading_text"
          />
        </Modal>
      </div>

      <div class="container">
        <div v-if="content.draw_enable_robot_selfie && content.share_intro" id="nav-share" class="nav-observer-section">
          <section class="home-image-section" id="share">
            <div class="image">
              <Swapsies ref="envelopeSwapper" delay="1300" start-method="startSpin" in-effect="fadeIn">
                <EnvelopeImage slot="start" />

                <div slot="end">
                    <h3>{{ content.share_thank_you_heading }}</h3>
                    <p>{{ content.share_thank_you_check_email }}</p>
                </div>
              </Swapsies>
            </div>

            <div class="content">
              <div v-html="content.share_intro"></div>

              <div v-if="hasSVG" class="share-form-ready">
                <div v-if="content.share_content" v-html="content.share_content">
                </div>

                <ShareForm
                  @sent="handleShareFormSent"
                  :site="site"
                  :siteName="isRobotSelfieSite ? 'Robot Selfie' : 'Portrait Without Borders'"
                  :shareFormNameLabel="content.share_form_name_label"
                  :shareFormEmailLabel="content.share_form_email_label"
                  :shareFormPostcodeLabel="content.share_form_postcode_label"
                  :shareFormTermsLabelStart="content.share_form_terms_label_start"
                  :shareFormTermsLabelEnd="content.share_form_terms_label_end"
                  :shareFormConsentLabel="content.share_form_consent_label"
                  :shareFormSubscribeLabel="content.share_form_subscribe_label"
                  :shareFormPrivacyLinkText="content.share_form_privacy_link_text"
                  :shareFormSendButton="content.share_form_send_button"
                  :shareFormSendButtonDisabled="content.share_form_send_button_disabled"
                  :shareFormErrorHeading="content.share_form_error_heading"
                  :shareFormTimeoutError="content.share_form_timeout_error"
                  :shareFormServerError="content.share_form_submit_error"
                  :modalCloseButton="content.share_form_close_popup"
                >
                  <div v-html="content.terms_and_conditions" slot="termsSlot"></div>
                  <div v-html="content.privacy_policy" slot="privacySlot"></div>
                </ShareForm>
              </div>

              <div v-else class="share-not-ready" v-html="content.share_holding_content">
              </div>

            </div>
          </section>
        </div>

        <section
          v-if="content.gallery_content || content.gallery_images.length"
          class="home-image-section no-image"
          id="gallery"
        >
          <div v-if="content.gallery_content" id="gallery-content" v-html="content.gallery_content"></div>
          <Gallery v-if="content.gallery_images.length" id="gallery-images" :images="content.gallery_images" />
        </section>

        <section
          v-if="content.enable_shop && content.shop_position === 'above_watch'"
          class="home-image-section no-image" id="store"
        >
          <Shop
            :site="site"
            :shopDigitalTitle="content.shop_digital_title"
            :shopPrintTitle="content.shop_print_title"
            :stripePublicKey="content.stripe_public_key"
          >

            <div v-html="content.shop_intro" slot="shop-intro"> </div>
            <div v-html="content.shop_digital_description" slot="shop-digital-description"> </div>
            <div v-html="content.shop_print_description" slot="shop-print-description"> </div>
            <div v-html="content.shop_checkout_intro" slot="shop-checkout-intro"> </div>
            <div v-html="content.shop_checkout_error" slot="shop-checkout-error"> </div>
            <div v-html="content.shop_checkout_success" slot="shop-checkout-success"> </div>

          </Shop>
        </section>


        <section v-if="content.watch_content" class="home-image-section image-right" id="watch">
          <div class="image">
            <EyeImage />
          </div>

          <div class="content" v-html="content.watch_content"></div>
        </section>

        <section class="home-image-section no-image" id="stream">
          <Stream
            v-if="showStream"
            :youTubeId="content.youtube_video_id"
            :hostedVideoUrl="content.hosted_video_url"
            :placeholderSrc="this.placeholderSrc"
          />
        </section>

        <div v-if="content.media_items.length" class="home-image-section no-image" id="media-items">
          <div class="media-items">
            <MediaItem
              v-for="(media, index) in content.media_items"
              :key="index"
              :youTubeId="media.youtube_video_id"
              :hostedVideoUrl="media.hosted_video_url"
            />
          </div>
        </div>


        <section
          v-if="content.enable_shop && content.shop_position === 'below_watch'"
          class="home-image-section no-image" id="shop"
        >
          <Shop
            :site="site"
            :shopDigitalTitle="content.shop_digital_title"
            :shopPrintTitle="content.shop_print_title"
            :stripePublicKey="content.stripe_public_key"
          >

            <div v-html="content.shop_intro" slot="shop-intro"> </div>
            <div v-html="content.shop_digital_description" slot="shop-digital-description"> </div>
            <div v-html="content.shop_print_description" slot="shop-print-description"> </div>
            <div v-html="content.shop_checkout_intro" slot="shop-checkout-intro"> </div>
            <div v-html="content.shop_checkout_error" slot="shop-checkout-error"> </div>
            <div v-html="content.shop_checkout_success" slot="shop-checkout-success"> </div>

          </Shop>
        </section>

        <div id="nav-faqs" class="nav-observer-section faqs-section">

          <section class="home-image-section no-image" id="faqs">
            <h2>{{ content.nav_faqs_label }}</h2>
            <FAQs :site="site" />
          </section>

        </div>

      </div>
      <SiteFooter :images="content.footer_images">
        <div v-html="content.footer_content"></div>
      </SiteFooter>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

import Confirmed from "@/components/Confirmed.vue"
import SiteHeader from "@/components/SiteHeader.vue"
import SectionTop from "@/components/SectionTop.vue"
import ShareForm from "@/components/ShareForm.vue"
import FAQs from "@/components/FAQs.vue"
import SwitchButton from "@/components/SwitchButton.vue"
import SiteFooter from "@/components/SiteFooter.vue"
import Stream from "@/components/Stream.vue"
import RobotSelfie from "@/components/RobotSelfie.vue"
import Modal from "@/components/Modal.vue"
import EnvelopeImage from "@/components/EnvelopeImage.vue"
import LenseImage from "@/components/LenseImage.vue"
import EyeImage from "@/components/EyeImage.vue"
import LoadingSpinner from "@/components/LoadingSpinner.vue"
import Shop from "@/components/Shop.vue"
import Swapsies from "@/components/Swapsies.vue"
import MediaItem from "@/components/MediaItem.vue"
import Gallery from "@/components/Gallery.vue"

export default {
  name: 'home',
  metaInfo () {
    return {
      title: this.meta.title,
      meta: [ { name: 'description', content: this.meta.description } ],
      htmlAttrs: {
        dir: this.language === 'ar' ? 'rtl' : 'ltr',
        lang: this.language,
      }
    };
  },
  components: {
    Confirmed,
    SiteHeader,
    SectionTop,
    ShareForm,
    FAQs,
    SwitchButton,
    SiteFooter,
    Stream,
    RobotSelfie,
    Modal,
    LoadingSpinner,
    Shop,
    Swapsies,
    MediaItem,
    Gallery,
    LenseImage,
    EnvelopeImage,
    EyeImage,
  },
  data() {
    return {
      path: "",
      site: "",
      meta: {},
      language: null,
      content: null,
      robotSelfieModalShowing: false,
      hasSVG: false,
      loading: true,
      error: null,
      confirmed: false,
      shareFormSubmitted: false,
    }
  },
  computed: {
    isRobotSelfieSite () {
      // For now using site host to determine bg image and colour
      // We may want to move this to a wagtail config or .env on build
      const localIsRobotSite = false;

      if (localIsRobotSite) {
        return window.location.host === 'robotselfie.live'
              || window.location.host === 'localhost'
      } else {
        return window.location.host === 'robotselfie.live'
      }
    },
    defaultTitle () {
      return this.isRobotSelfieSite
        ? 'Robot Selfie - Have your portrait drawn by robots'
        : 'A portrait without borders - Have your portrait drawn by robots';
    },
    siteColour () {
      return this.isRobotSelfieSite
        // Keep in case we ever want robotselfie & portrait bg to differ again
        ? "#ffffff"
        : "#ffffff"
    },
    backgroundImage () {
        if (this.isRobotSelfieSite) {
        return { src: '/robotselfie-header.png', fullwidth: true };
        } else {return { src: '/portrait-header.png', fullwidth: false };
      }
    },
    placeholderSrc () {
      if (this.isRobotSelfieSite) {
        return null;
      }
      return '/robot-art.jpg';
    },
    showStream () {
      // Only show stream if we have content for it
      return this.content.youtube_video_id 
            || this.content.hosted_video_url 
            || this.placeholderSrc;
    },
    navLinks () {
      return [
        {
          href: '#draw',
          observe: ['section-top', 'start', 'draw'],
          text: this.content.nav_draw_label,
        },
        {
          href: '#share',
          observe: ['nav-share'],
          text: this.content.nav_share_label,
        },
        {
          href: '#gallery',
          observe: ['gallery-content', 'gallery-images'],
          text: this.content.nav_gallery_label,
        },
        {
          href: '#store',
          observe: ['store'],
          text: this.content.nav_shop_label,
        },
        {
          href: '#watch',
          observe: ['watch', 'stream', 'media-items'],
          text: this.content.nav_watch_label,
        },
        {
          href: '#shop',
          observe: ['shop'],
          text: this.content.nav_shop_label,
        },
        {
          href: '#faqs',
          observe: ['nav-faqs'],
          text: this.content.nav_faqs_label,
        },
      ]
    },
    hasTranslationLinks () {
      return this.translationLinks.length > 0;
    },
    // Lang chooser hardcoded for first couple of instances
    translationLinks () {
      if (window.location.pathname.includes('tarrega')) {
        return [
          {
            href: '/tarrega-cas',
            text: 'Castellano',
          },
          {
            href: '/tarrega-cat',
            text: 'Català',
          },
          {
            href: '/tarrega-en',
            text: 'English',
          },
        ];
      }
      if (window.location.pathname.includes('dhahran')) {
        return [
          {
            href: '/dhahran-ar',
            text: 'العربية',
          },
          {
            href: '/dhahran-en',
            text: 'English',
          },

        ];
      }
      return [];
    }
  },
  created() {
    const uri = window.location.search.substring(1);
    const params = new URLSearchParams(uri);
    this.confirmed = params.get("confirmed") === "" || params.get("confirmed");
  },
  mounted: function() {
    this.path = this.$router.currentRoute.path
    this.site = this.path.replace(/\//g, "")

    this.loading = true
    axios.get("/cms/api/v2/pages/find/?html_path=" + this.path + "&fields=*")
      .then((response) => {
        this.content = response.data
        const hasTitle = this.content.meta && this.content.meta.seo_title !== '';
        const hasDescription = this.content.meta && this.content.meta.search_description !== '';
        this.meta = {
          title: hasTitle ? this.content.meta.seo_title : this.defaultTitle,
          description: hasDescription ? this.content.meta.search_description : 'Kaleider invites you to have your picture drawn by robots',
        }
        this.language = this.getLanguage(window.location.pathname);
        this.loading = false

        const translateScript = document.createElement('script')
        translateScript.setAttribute("src", "https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit")
        document.body.appendChild(translateScript)
      })
      .catch((err) => {
        console.log(err)
        this.loading = false
        this.error = err.response.status
      })
  },
  methods: {
    gotoAnchor(anchor) {
      var url = location.href;
      location.href = "#" + anchor;
      history.replaceState(null,null,url);
    },
    toggleRobotSelfieModal() {
      this.$refs.robotSelfieModal.onShow = this.$refs.robotSelfie.enableVideo;
      this.$refs.robotSelfieModal.onHide = this.$refs.robotSelfie.stopVideo;
      this.$refs.robotSelfieModal.toggle();
      this.robotSelfieModalShowing = this.$refs.robotSelfieModal.visible;
    },
    handleSelfieSaved() {
      this.hasSVG = true;
      this.gotoAnchor("share");
    },
    handleShareFormSent() {
      this.shareFormSubmitted = true;
      this.swapEnvelope();
    },
    swapEnvelope () {
      this.$refs.envelopeSwapper.swap();
    },
    getLanguage(path) {
      if (this.hasTranslationLinks) {
        return path.split('-')[1];
      }
      return "en";
    }
  },
}


</script>

<style lang="scss" scoped>

#home {

  &.has-translations {
    @media screen and (max-width: 1200px) {
      padding-top: 45px;
    }
  }

  .loading {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    flex-flow: row nowrap;
    justify-content: center;
    align-items: center;

    .loading-spinner {
      opacity: 0;
      animation: fade-part-in 0.5s 0.5s forwards;
    }
  }

  @keyframes fade-part-in {
    0% {
      opacity: 0;
    }
    100% {
      opacity: 0.3;
    }
  }

  @keyframes fade-fully-in {
    0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }

  .share-form-ready {
    max-height: 0;
    overflow: hidden;
    animation: share-form-reveal 1s 0.2s ease forwards;
  }

  @keyframes share-form-reveal {
    0% {
      max-height: 0;
    }
    100% {
      max-height: 1000px;
    }
  }

  .nav-observer-section {
    margin-top: 0;
    padding-top: 10px;
  }

  .faqs-section {
    padding-bottom: 50px;
  }


  .home-image-section {
    display: flex;
    flex-flow: row nowrap;
    justify-content: space-between;
    align-items: center;
    margin: 0;
    padding: 50px 0 0 0;

    &.image-right {
      flex-direction: row-reverse;
    }

    &, &.image-right {
      @media screen and (max-width: $break-mobile) {
        flex-direction: column;
        justify-content: flex-start;
        align-items: flex-start;
      }
    }


    &.no-image {
      display: block;
    }

    .image {
      width: $home-col-width;

      @media screen and (max-width: $break-mobile) {
        width: 60%;
        margin: 0 auto 2em auto;
      }

      img {
        display: block;
        max-width: 100%;
        height: auto;
      }

    }
    .content {
      width: $home-col-width;

      @media screen and (max-width: $break-mobile) {
        width: auto;
      }

    }
  }

  .media-items {
    display: flex;
    flex-flow: row wrap;
    justify-content: space-between;
    align-items: flex-start;

    @media screen and (max-width: $break-mobile) {
      flex-flow: column nowrap;
    }

    iframe {
      width: calc(50% - 0.5em);
      margin-bottom: 1em;

      @media screen and (max-width: $break-mobile) {
        width: 100%;
      }
    }
  }


}
</style>
